import { useCallback, useMemo } from "react"
import { useSearchParams } from "react-router-dom"

import type { PerformanceGrain } from "../types"
import type { Compare, MemberQuery, MemberView, ProjectFlag } from "./types"

const GRAINS: PerformanceGrain[] = ["month", "quarter", "year"]
const VIEWS: MemberView[] = ["overview", "projects", "types", "milestones"]
const COMPARES: Compare[] = ["company", "previous_period", "previous_year"]
const FLAGS: ProjectFlag[] = [
  "on_time",
  "late_1_7",
  "late_8_14",
  "late_over_14",
  "revenue_gap",
  "cost_overrun",
  "negative_margin",
  "missing_inputs",
  "schedule_exceptions",
  "financial_exceptions"
]
const METRICS = [
  "schedule_score",
  "financial_score",
  "on_time_percent",
  "avg_delay_days",
  "overrun_amount",
  "gap_amount",
  "margin_variance_points"
] as const

export type TrendMetric =
  | "schedule_score"
  | "financial_score"
  | "on_time_percent"
  | "avg_delay_days"
  | "overrun_amount"
  | "gap_amount"
  | "margin_variance_points"

export interface MemberUrlPatch {
  grain?: PerformanceGrain
  from?: string | null
  to?: string | null
  projectType?: string | null
  view?: MemberView
  compare?: Compare
  metric?: TrendMetric
  period?: string | null
  milestone?: string | null
  flag?: ProjectFlag | null
  min?: number | null
}

const pick = <T extends string>(
  value: string | null,
  allowed: readonly T[],
  fallback: T
) => (allowed.includes(value as T) ? (value as T) : fallback)

// The member page keeps every choice in the URL so Back, refresh and a shared
// link all reproduce the view (spec §3 "State persistence").
export function useMemberUrlState() {
  const [params, setParams] = useSearchParams()

  const query: MemberQuery = useMemo(
    () => ({
      grain: pick(params.get("grain"), GRAINS, "quarter"),
      from: params.get("from") ?? undefined,
      to: params.get("to") ?? undefined,
      projectType: params.get("type") ?? undefined,
      compare: pick(params.get("compare"), COMPARES, "company"),
      period: params.get("period") ?? undefined,
      milestone: params.get("milestone") ?? undefined,
      flag: FLAGS.includes(params.get("flag") as ProjectFlag)
        ? (params.get("flag") as ProjectFlag)
        : undefined,
      minOccurrences: Number(params.get("min")) || 3
    }),
    [params]
  )
  const view = pick(params.get("view"), VIEWS, "overview")
  const metric = pick(
    params.get("metric"),
    METRICS,
    "schedule_score"
  ) as TrendMetric

  const update = useCallback(
    (patch: MemberUrlPatch) =>
      setParams((prev) => {
        const next = new URLSearchParams(prev)
        const put = (key: string, value: string | number | null | undefined) =>
          value === null || value === undefined || value === ""
            ? next.delete(key)
            : next.set(key, String(value))
        if (patch.grain !== undefined && patch.grain !== prev.get("grain")) {
          next.delete("from")
          next.delete("to")
          next.delete("period")
          put("grain", patch.grain)
        }
        if (patch.from !== undefined) put("from", patch.from)
        if (patch.to !== undefined) put("to", patch.to)
        if (patch.projectType !== undefined) put("type", patch.projectType)
        if (patch.view !== undefined)
          put("view", patch.view === "overview" ? null : patch.view)
        if (patch.compare !== undefined)
          put("compare", patch.compare === "company" ? null : patch.compare)
        if (patch.metric !== undefined) put("metric", patch.metric)
        if (patch.period !== undefined) put("period", patch.period)
        if (patch.milestone !== undefined) put("milestone", patch.milestone)
        if (patch.flag !== undefined) put("flag", patch.flag)
        if (patch.min !== undefined) put("min", patch.min)
        return next
      }),
    [setParams]
  )

  return { query, view, metric, update }
}
